import type { SubSpecId } from '../model/types';

/** Plain-language sub-spec copy. The Build card and the Rules screen both read this. */
export interface SpecCopy {
  /** One line under the sub-spec name. */
  line: string;
  /** Longer note behind "More". */
  detail: string;
}

export const SPEC_COPY: Record<SubSpecId, SpecCopy> = {
  bulwark: {
    line: 'Blocks hits with a tower shield, then spends that stored guard as a shockwave.',
    detail:
      'Rampart stores the hits you block. Shockwave spends that guard on the wave in front of you. Higher ranks enlarge the shield, slow the wave, and let a full Rampart ignore one heavy hit.',
  },
  chainwarden: {
    line: 'Pulls distant foes in with hooks and holds them on a chain.',
    detail:
      'Hook drags the farthest foe closer, and Tether keeps it there. Drag In hauls the pack onto you. Ranks pull farther and hold longer.',
  },
  forgeheart: {
    line: 'Heats its armor so hits bounce back, and that heat costs health.',
    detail:
      'Armor grows hotter between hits. Hotter armor reflects more of the blow, and the heat drains your own health. Ranks raise the reflection and the cost.',
  },
  duelist: {
    line: 'Draws a dueling circle and hits harder against one marked foe.',
    detail:
      'The Red Line marks a single target. Damage against that foe rises while the circle holds. Ranks deepen the mark and the bonus.',
  },
  siegebreaker: {
    line: 'Lands slow, heavy blows that break armor.',
    detail:
      'Each swing is slow and strips armor before the hit lands. Ranks break more armor and make the follow-up hit harder.',
  },
  banner: {
    line: 'Plants a war banner that steadies the bearer.',
    detail:
      'The banner is a fixed point. While it stands, you take less harm and recover your footing faster. Ranks widen that steady ground.',
  },
  quartermaster: {
    line: 'Drops crates of ammo, potions, and temporary shields onto the field.',
    detail:
      'Supply crates land where you fight. Opening one restocks a shot, a potion, or a short shield. Ranks drop crates more often and make each one richer.',
  },
  oathkeeper: {
    line: 'Swears an oath before the fight: kept, it spikes power; broken, it costs you.',
    detail:
      'You name an oath at the start. Keeping it through the fight adds a power spike. Breaking it applies a penalty. Ranks raise both the spike and the cost.',
  },
  loomcaster: {
    line: 'Threads enemies together so damage to one runs along the thread.',
    detail:
      'A thread ties foes to each other. Part of the damage dealt to one spreads to the rest. Ranks add threads and increase the share that spreads.',
  },
  hourglass: {
    line: 'Rewinds an ally a few seconds, putting back health and place.',
    detail:
      'Rewind restores health from a few seconds ago and returns the ally to where they stood. Still delays the next enemy hit. Ranks lengthen the rewind and can pull you back through one heavy hit.',
  },
  foldwalker: {
    line: 'Steps through a short fold and drags nearby foes through with it.',
    detail:
      'The step is a short teleport. Foes standing close are pulled through the same fold. Ranks lengthen the step and widen who gets dragged.',
  },
  omen: {
    line: 'Shows an attack before it lands, and rewards allies who dodge it.',
    detail:
      'You see the next strike early enough to step aside. An ally who dodges the shown attack gains a short buff. Ranks show the strike sooner and strengthen that buff.',
  },
  stillpoint: {
    line: 'Freezes a patch of the field, then bursts everyone left inside it.',
    detail:
      'The frozen zone holds foes in place. When the freeze ends, everyone still inside takes the burst at once. Ranks widen the zone and the burst.',
  },
  mirrortailor: {
    line: 'Stitches a decoy that draws attacks and copies one skill.',
    detail:
      'The decoy pulls aggro off you and repeats one of your skills. Ranks make the copy last longer and draw more of the wave.',
  },
  unraveler: {
    line: 'Strips buffs and shields, then turns what it removed into damage.',
    detail:
      'Buffs and shields come off the target. The strength removed is added to your next hit. Ranks strip more and convert a larger share.',
  },
  fatebinder: {
    line: 'Links two allies so they share the damage and the healing.',
    detail:
      'A link ties two allies together. A share of damage taken by one moves to the other, and healing flows the same way. Ranks raise the share that is shared.',
  },
  rootmother: {
    line: 'Grows a healing grove that mends more the longer it stands.',
    detail:
      'The grove heals anyone standing in it. Each moment it survives, the healing grows. Ranks speed that growth and let the grove last longer.',
  },
  packcaller: {
    line: 'Calls a pack of three beasts and gives each one its own order.',
    detail:
      'Three beasts fight with you. Each can be told to bite, guard, or harass on its own. Ranks make the pack hit harder and follow orders faster.',
  },
  barkhide: {
    line: 'Wears layers of bark that peel off into thorny ground.',
    detail:
      'Each layer absorbs a hit, then falls as thorns under the attacker. Ranks add layers and make the fallen bark hurt more.',
  },
  sporelord: {
    line: 'Spreads spore clouds that stack poison and confusion.',
    detail:
      'Spores hang in a cloud. Foes who stay in it gain poison and start to miss. Ranks thicken the cloud and raise the stacks.',
  },
  seasonturner: {
    line: 'Cycles Spring, Summer, Autumn, and Winter, and each stance fights differently.',
    detail:
      'Spring mends, Summer burns, Autumn strips, and Winter slows. You move through the four in order. Ranks strengthen the stance you are in.',
  },
  stormhorn: {
    line: 'Builds a storm by moving, then calls the lightning down.',
    detail:
      'Movement fills the storm. At full charge the lightning strikes the field. Ranks charge faster and make the strike hit harder.',
  },
  mosswarden: {
    line: 'Takes a moss-golem form that moves slowly and heals from the ground.',
    detail:
      'The golem form is slow and hard to move. Standing on the field mends you. Ranks add health to the form and speed the mending.',
  },
  hivekeeper: {
    line: 'Sends bee swarms that carry buffs to allies or stings to foes.',
    detail:
      'A swarm can be aimed at an ally to leave a buff, or at a foe to sting. Ranks enlarge the swarm and let it do both in one flight.',
  },
  debtcollector: {
    line: 'Turns damage it takes into Debt, then calls that Debt in as a burst.',
    detail:
      'Hits against you add Debt instead of only hurting. Calling the Debt in spends it as burst damage. Ranks raise how much damage becomes Debt and how hard the burst hits.',
  },
  echo: {
    line: 'Leaves after-images that repeat the last strike.',
    detail:
      'Each image plays your last hit again. Ranks add images and make the repeated strike hit closer to the original.',
  },
  lanternless: {
    line: 'Fades out in the dark and snuffs nearby lights.',
    detail:
      'Darkness hides you, and you put out lights to spread it. Foes lose track of you while the dark holds. Ranks deepen the dark and lengthen the fade.',
  },
  hollowshepherd: {
    line: 'Raises fallen foes as short-lived thralls.',
    detail:
      'A foe that falls can be pulled back up to fight for you. The thrall does not last long. Ranks raise more of them and keep each one a little longer.',
  },
  grave: {
    line: 'Mends with shadow when the braid starts to fray.',
    detail:
      'As your health falls, shadow closes the wounds. The mend is strongest when the braid is close to breaking. Ranks increase that shadow mend.',
  },
  umbralanchor: {
    line: 'Becomes a shadow pillar that soaks damage and roots attackers.',
    detail:
      'The pillar takes the hits aimed at you. Anyone who strikes it is rooted in place. Ranks let the pillar soak more and hold the root longer.',
  },
  whisperthief: {
    line: 'Steals one enemy skill and uses it a single time.',
    detail:
      'You take a skill off a foe and cast it once as your own. The stolen skill is then gone. Ranks let you steal a stronger skill.',
  },
  dusk: {
    line: 'Lays curses that bite every foe standing nearby.',
    detail:
      'A curse sits on the pack around you and ticks on each of them. Ranks add curses and let them spread farther through the wave.',
  },
};
